import React, { useState, useEffect, useRef } from 'react';
import { api } from '../services/api';

export function AiPage() {
  const [users, setUsers] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [weeklyData, setWeeklyData] = useState<any>(null);
  const [anomalies, setAnomalies] = useState<any[]>([]);
  const [aiSummary, setAiSummary] = useState('');
  const [loadingWeekly, setLoadingWeekly] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [chatMessages, setChatMessages] = useState<{ role: 'user' | 'ai'; text: string }[]>([
    { role: 'ai', text: '¡Hola! Soy el asistente IA de AccioTrack. Puedo analizar la productividad del equipo, comparar semanas, detectar anomalías y darte recomendaciones. ¿En qué te puedo ayudar?' }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api.get('/users').then(r => setUsers(r.data));
    api.get('/ai/anomalies').then(r => setAnomalies(r.data));
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const loadWeeklyComparison = async () => {
    setLoadingWeekly(true);
    try {
      const url = selectedUser ? `/reports/weekly-comparison?userId=${selectedUser}` : '/reports/weekly-comparison';
      const { data } = await api.get(url);
      setWeeklyData(data);
    } finally {
      setLoadingWeekly(false);
    }
  };

  const loadAiSummary = async () => {
    if (!selectedUser) return;
    setLoadingSummary(true);
    try {
      const { data } = await api.get(`/ai/daily-summary?userId=${selectedUser}&date=${new Date().toISOString().split('T')[0]}`);
      setAiSummary(data.summary || '');
    } finally {
      setLoadingSummary(false);
    }
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim() || chatLoading) return;
    const msg = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', text: msg }]);
    setChatLoading(true);

    try {
      // Build context from anomalies and weekly data
      const context = `Datos del equipo AccioSoft:
- Anomalías detectadas: ${anomalies.length > 0 ? anomalies.map(a => a.message).join(', ') : 'ninguna'}
- Empleados: ${users.map(u => u.name).join(', ')}
${weeklyData ? `- Semana actual: ${weeklyData.thisWeek?.totalHours}h, ${weeklyData.thisWeek?.productivityPct}% productividad` : ''}
${weeklyData ? `- Semana anterior: ${weeklyData.lastWeek?.totalHours}h, ${weeklyData.lastWeek?.productivityPct}% productividad` : ''}`;

      const { data } = await api.post('/ai/chat', { message: msg, context });
      setChatMessages(prev => [...prev, { role: 'ai', text: data.response || 'No se pudo obtener respuesta.' }]);
    } catch {
      setChatMessages(prev => [...prev, { role: 'ai', text: 'Error al conectar con la IA. Inténtalo de nuevo.' }]);
    } finally {
      setChatLoading(false);
    }
  };

  const formatChange = (val: number, unit = '') => {
    const sign = val > 0 ? '+' : '';
    const color = val > 0 ? 'var(--green)' : val < 0 ? 'var(--red)' : 'var(--text3)';
    return <span style={{ color, fontWeight: 600 }}>{sign}{val}{unit}</span>;
  };

  return (
    <div style={{ padding: '28px 32px' }}>
      <div className="page-header">
        <div>
          <h1 className="page-title">✨ Inteligencia Artificial</h1>
          <p className="page-subtitle">Análisis inteligente, comparativas y recomendaciones</p>
        </div>
        <select className="input" style={{ width: 200 }} value={selectedUser} onChange={e => setSelectedUser(e.target.value)}>
          <option value="">Seleccionar empleado</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      </div>

      {/* Anomalies */}
      {anomalies.length > 0 && (
        <div className="card" style={{ marginBottom: 20, borderColor: 'rgba(239,68,68,0.25)', background: 'rgba(239,68,68,0.04)' }}>
          <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--red)', marginBottom: 12 }}>🚨 Anomalías detectadas hoy</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {anomalies.map((a, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                <span style={{ color: a.severity === 'warning' ? 'var(--accent)' : 'var(--red)' }}>
                  {a.severity === 'warning' ? '⚠️' : '🔴'}
                </span>
                <span>{a.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        {/* Weekly comparison */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <h3 style={{ fontSize: 13, fontWeight: 700 }}>📈 Comparativa semanal</h3>
            <button className="btn btn-secondary" style={{ fontSize: 11, padding: '5px 10px' }} onClick={loadWeeklyComparison} disabled={loadingWeekly}>
              {loadingWeekly ? '...' : 'Cargar'}
            </button>
          </div>
          {weeklyData ? (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                {[
                  { label: 'Horas esta semana', value: `${weeklyData.thisWeek?.totalHours || 0}h`, change: weeklyData.hoursChange, unit: 'h' },
                  { label: 'Productividad', value: `${weeklyData.thisWeek?.productivityPct || 0}%`, change: weeklyData.productivityChange, unit: '%' },
                ].map(s => (
                  <div key={s.label} style={{ background: 'var(--bg3)', borderRadius: 10, padding: 12 }}>
                    <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 4 }}>{s.label}</div>
                    <div style={{ fontSize: 20, fontWeight: 700 }}>{s.value}</div>
                    <div style={{ fontSize: 11, marginTop: 2 }}>vs semana anterior: {formatChange(s.change, s.unit)}</div>
                  </div>
                ))}
              </div>
              {/* Top apps comparison */}
              <div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8 }}>Apps más usadas esta semana</div>
                {(weeklyData.thisWeekApps || []).slice(0, 4).map((app: any, i: number) => {
                  const h = Math.floor(app.totalSecs / 3600);
                  const m = Math.floor((app.totalSecs % 3600) / 60);
                  return (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, padding: '3px 0' }}>
                      <span>{app.appName}</span>
                      <span style={{ color: 'var(--accent)' }}>{h > 0 ? `${h}h ` : ''}{m}m</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text3)', fontSize: 12 }}>
              Selecciona un empleado y pulsa "Cargar"
            </div>
          )}
        </div>

        {/* AI Daily Summary */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <h3 style={{ fontSize: 13, fontWeight: 700 }}>🤖 Resumen IA del día</h3>
            <button className="btn btn-secondary" style={{ fontSize: 11, padding: '5px 10px' }} onClick={loadAiSummary} disabled={loadingSummary || !selectedUser}>
              {loadingSummary ? '...' : 'Generar'}
            </button>
          </div>
          {aiSummary ? (
            <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{aiSummary}</div>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text3)', fontSize: 12 }}>
              {selectedUser ? 'Pulsa "Generar" para obtener el resumen IA' : 'Selecciona un empleado primero'}
            </div>
          )}
        </div>
      </div>

      {/* AI Chat */}
      <div className="card">
        <h3 style={{ fontSize: 13, fontWeight: 700, marginBottom: 16 }}>💬 Chat con AccioTrack IA</h3>
        <div style={{
          height: 300, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10,
          padding: '0 4px', marginBottom: 12,
        }}>
          {chatMessages.map((msg, i) => (
            <div key={i} style={{
              display: 'flex',
              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
            }}>
              <div style={{
                maxWidth: '75%',
                padding: '10px 14px',
                borderRadius: msg.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                background: msg.role === 'user' ? 'var(--accent)' : 'var(--bg3)',
                color: msg.role === 'user' ? '#000' : 'var(--text)',
                fontSize: 13,
                lineHeight: 1.5,
              }}>
                {msg.text}
              </div>
            </div>
          ))}
          {chatLoading && (
            <div style={{ display: 'flex', gap: 4, padding: '8px 14px' }}>
              {[0, 1, 2].map(i => (
                <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--text3)', animation: `bounce 1s ${i * 0.2}s infinite` }} />
              ))}
            </div>
          )}
          <div ref={chatEndRef} />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            className="input"
            placeholder="Pregunta sobre el equipo, productividad, anomalías..."
            value={chatInput}
            onChange={e => setChatInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && sendChatMessage()}
          />
          <button className="btn btn-primary" onClick={sendChatMessage} disabled={chatLoading || !chatInput.trim()}>
            Enviar
          </button>
        </div>
        <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {['¿Quién es más productivo hoy?', '¿Hay anomalías esta semana?', 'Resume la actividad del equipo', '¿Quién lleva más horas?'].map(q => (
            <button key={q} onClick={() => { setChatInput(q); }} style={{
              background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 20,
              padding: '4px 10px', fontSize: 11, color: 'var(--text2)', cursor: 'pointer',
            }}>{q}</button>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes bounce { 0%, 80%, 100% { transform: scale(0); } 40% { transform: scale(1); } }
      `}</style>
    </div>
  );
}
